// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import ProfileCommunityUserGrid from 'app/common/react/components/profile/ProfileCommunity/ProfileCommunityUserGrid';
import SectionHeader from 'app/common/react/components/profile/ProfileCommunity/SectionHeader';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import { Box, Container, Text, Spinner } from 'gestalt';

const WRAPPER_VERTICAL_PADDING = 270;

const GRID_WIDTH = 744;

const GRID_LOAD_DELAY = 10;

const PREVIEW_USER_LIMIT = 2;

// Ensuring proper tricky layering with menu flyout with modal stacked on top.
const styles = {
  header: {
    zIndex: 1,
  },
};

type Props = {|
  isOwnProfile: boolean,
  previewUserLimit?: number,
  title: string,
  users: {
    data: Array<ProfileUser>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
|};

type State = {|
  isModalOpen: boolean,
  isMounted: boolean,
  scrollContainer: ?HTMLElement,
  showGrid: boolean,
  wrapperHeight: ?number,
|};

class ProfileCommunityUsers extends React.PureComponent<Props, State> {
  state = {
    isModalOpen: false,
    isMounted: false,
    showGrid: false,
    scrollContainer: null,
    wrapperHeight: null,
  };

  componentDidMount() {
    this.updateHeight();
    window.addEventListener('resize', this.updateHeight);

    this.setState({
      isMounted: true,
    });

    this.showGridAfterDelay();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateHeight);
    this.setState({
      isMounted: false,
    });
  }

  setScrollContainer = (ref: ?HTMLElement) => {
    if (this.state.isMounted) {
      this.setState({
        scrollContainer: ref,
      });
    }
  };

  showGridAfterDelay = () => {
    if (this.state.isMounted) {
      this.setState({
        showGrid: false,
      });
    }

    setTimeout(() => {
      if (this.state.isMounted) {
        this.setState({
          showGrid: true,
        });
      }
    }, GRID_LOAD_DELAY);
  };

  updateHeight = () => {
    const height = window.innerHeight - WRAPPER_VERTICAL_PADDING;
    this.setState({
      wrapperHeight: height > 0 ? height : null,
    });
  };

  handleOpenModal = () => {
    this.setState({
      isModalOpen: true,
    });
  };

  handleDoneModal = () => {
    const { users } = this.props;

    this.setState({
      isModalOpen: false,
    });
    users.refreshData();
    this.showGridAfterDelay();
  };

  render() {
    const { users, isOwnProfile, title, previewUserLimit } = this.props;
    const { isModalOpen, showGrid, scrollContainer, wrapperHeight } = this.state;

    if (!users) {
      return null;
    }

    const userPreviewGrid = showGrid ? (
      <ProfileCommunityUserGrid
        users={users}
        isOwnProfile={isOwnProfile}
        scrollContainer={scrollContainer}
        limit={previewUserLimit || PREVIEW_USER_LIMIT}
      />
    ) : (
      <Box display="flex" justifyContent="center" padding={7}>
        <Spinner accessibilityLabel={i18n._('Loading ...', 'Loading indicator')} show />
      </Box>
    );
    const userGrid = showGrid ? (
      <ProfileCommunityUserGrid
        users={users}
        isOwnProfile={isOwnProfile}
        scrollContainer={scrollContainer}
      />
    ) : (
      <Box display="flex" justifyContent="center" padding={7}>
        <Spinner accessibilityLabel={i18n._('Loading ...', 'Loading indicator')} show />
      </Box>
    );

    return (
      <Box>
        <Box alignItems="center" justifyContent="between" alignContent="center">
          <Container>
            <Box padding={3}>
              <SectionHeader label={title} onSeeAllButtonClick={() => this.handleOpenModal()} />
            </Box>
            <Box marginLeft={7} width={GRID_WIDTH}>
              {userPreviewGrid}
            </Box>
          </Container>
        </Box>
        <Box>
          {isModalOpen && (
            <Modal
              accessibilityCloseLabel="close"
              accessibilityModalLabel={i18n._(
                'Manage user list',
                'Accessibility label for managing user list',
              )}
              size="lg"
              heading={
                <Box display="flex" justifyContent="center" alignItems="center" paddingY={4}>
                  <Text size="xl" weight="bold">
                    {title}
                  </Text>
                </Box>
              }
              onDismiss={this.handleDoneModal}
            >
              <Box
                display="flex"
                direction="column"
                height={wrapperHeight}
                dangerouslySetInlineStyle={{ __style: styles.header }}
              >
                <Box
                  display="flex"
                  flex="none"
                  overflow="scrollY"
                  paddingY={1}
                  marginBottom={4}
                  ref={this.setScrollContainer}
                  justifyContent="center"
                >
                  <Box width={GRID_WIDTH} height="100%">
                    {userGrid}
                  </Box>
                </Box>
              </Box>
            </Modal>
          )}
        </Box>
      </Box>
    );
  }
}

export default ProfileCommunityUsers;
